import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Download, Mail, RefreshCw, AlertTriangle } from 'lucide-react';
import { fetchPpbReport, downloadPpbCsv, emailPpbReport } from '../services/api';

// Reports → Daily PPB. Matt Sr's daily margin read.
//
// This page renders what the server computed and NOTHING ELSE. Every figure
// comes from lib/ppb-report.js, the same engine behind the emailed report, so
// the screen and the inbox cannot disagree about a day. If a number needs
// working out, it belongs in the engine — a second definition here is how the
// two versions start to drift.

const GOOD = '#1e7e34';
const BAD = '#c0392b';
const WARN = '#b8860b';

const usd = (v) => {
  const n = Number(v || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pctTxt = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);
const signColor = (v) => (Number(v) < 0 ? BAD : GOOD);
const marginColor = (p) => (p === null || p === undefined ? 'var(--gray-500)' : p < 25 ? BAD : p < 40 ? WARN : GOOD);

// MM/DD/YYYY in Eastern, matching the server's idea of a shipping day.
const fmtUs = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
function etYesterday() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  et.setDate(et.getDate() - 1);
  return et;
}
function parseUs(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || ''));
  return m ? new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])) : null;
}

function Tile({ label, value, color, note }) {
  return (
    <div className="card" style={{ flex: 1, minWidth: 170 }}>
      <div className="card-body" style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--navy)', marginTop: 4 }}>{value}</div>
        {note ? <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>{note}</div> : null}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2, fontWeight: 400 }}>{subtitle}</div> : null}
        </div>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

export default function DailyPpb({ navigateTo }) {
  const initial = (() => {
    // Deep link from the emailed report: ?page=daily-ppb&date=08/24/2026
    try {
      const q = new URLSearchParams(window.location.search).get('date');
      if (q && parseUs(q)) return parseUs(q);
    } catch { /* noop */ }
    return etYesterday();
  })();

  const [date, setDate] = useState(initial);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);

  const dateLabel = fmtUs(date);
  const shift = (n) => setDate((d) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; });

  const load = useCallback((withRefresh) => {
    let alive = true;
    setLoading(true); setError(null);
    if (withRefresh) setRefreshing(true);
    fetchPpbReport({ date: dateLabel, refresh: Boolean(withRefresh) })
      .then((r) => { if (alive) setReport(r); })
      .catch((e) => { if (alive) { setError(e.message); setReport(null); } })
      .finally(() => { if (alive) { setLoading(false); setRefreshing(false); } });
    return () => { alive = false; };
  }, [dateLabel]);

  useEffect(() => { setReport(null); return load(false); }, [load]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  const onCsv = async () => {
    setBusy('csv');
    try { await downloadPpbCsv(dateLabel); } catch (e) { showToast(`CSV failed: ${e.message}`); }
    setBusy('');
  };
  const onEmail = async () => {
    const to = window.prompt('Email this day\'s report to:', '');
    if (!to) return;
    setBusy('email');
    try { const r = await emailPpbReport(dateLabel, to.trim()); showToast(r.ok ? `Sent to ${to.trim()}.` : `Send failed: ${r.error || 'unknown'}`); }
    catch (e) { showToast(`Send failed: ${e.message}`); }
    setBusy('');
  };

  const trendMax = useMemo(() => {
    if (!report || !report.trend || !report.trend.length) return 1;
    return Math.max(...report.trend.map((p) => Math.max(p.meanPpb, p.medianPpb)), 1);
  }, [report]);

  const h = report && !report.empty ? report.headline : null;

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo && navigateTo('dashboard')}>Dashboard</a>
        <span> / </span><span>Reports</span><span> / </span><span>Daily PPB</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '4px 0 16px' }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)', fontSize: 22 }}>Profit per box</h1>
          <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
            {report ? report.label : dateLabel} — where the margin went, by product and by shipping
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => shift(-1)} title="Previous day"><ChevronLeft size={16} /></button>
          <input
            type="date"
            className="form-input"
            style={{ width: 160 }}
            value={`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`}
            onChange={(e) => {
              const [y, m, d] = e.target.value.split('-').map(Number);
              if (y && m && d) setDate(new Date(y, m - 1, d));
            }}
          />
          <button className="btn btn-ghost" onClick={() => shift(1)} title="Next day"><ChevronRight size={16} /></button>
          <button className="btn btn-outline" onClick={() => load(true)} disabled={refreshing} title="Re-pull production's report for this day, picking up costs entered since">
            <RefreshCw size={15} style={{ marginRight: 6, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing…' : 'Refresh costs'}
          </button>
          <button className="btn btn-outline" onClick={onCsv} disabled={busy === 'csv' || !report}>
            <Download size={15} style={{ marginRight: 6 }} />CSV
          </button>
          <button className="btn btn-primary" onClick={onEmail} disabled={busy === 'email' || !report}>
            <Mail size={15} style={{ marginRight: 6 }} />Email it
          </button>
        </div>
      </div>

      {toast ? (
        <div className="card" style={{ marginBottom: 14, borderLeft: '4px solid var(--orange)' }}>
          <div className="card-body" style={{ padding: '10px 14px', fontSize: 13 }}>{toast}</div>
        </div>
      ) : null}

      {error ? (
        <div className="card"><div className="card-body" style={{ color: BAD }}>Couldn't load this day: {error}</div></div>
      ) : loading && !report ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--gray-500)' }}>Building the day…</div></div>
      ) : !report ? null : report.empty ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--gray-500)' }}>No boxes shipped on {report.label}.</div></div>
      ) : (
        <>
          {report.notes && report.notes.length ? (
            <div className="card" style={{ marginBottom: 18, borderLeft: `4px solid ${WARN}` }}>
              <div className="card-body" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <AlertTriangle size={15} color={WARN} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                    Worth knowing about these numbers
                  </span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6, color: 'var(--gray-700)' }}>
                  {report.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            </div>
          ) : null}

          {/* 1 — headline */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Tile label="Average per box" value={usd(h.meanPpb)} color={signColor(h.meanPpb)} note={`${h.orders} boxes`} />
            <Tile label="Typical box" value={usd(h.medianPpb)} color={signColor(h.medianPpb)} note="median" />
            <Tile label="Total profit" value={usd(h.totalProfit)} color={signColor(h.totalProfit)} note={`on ${usd(h.totalRevenue)}`} />
            <Tile
              label="Margin"
              value={pctTxt(h.marginPct)}
              color={h.marginPct >= 20 ? GOOD : h.marginPct < 10 ? BAD : WARN}
              note={`costs in on ${h.costsEntered}/${h.orders}`}
            />
          </div>

          <Section
            title="Worst boxes"
            subtitle={
              h.spread < -8
                ? `The average sits ${usd(Math.abs(h.spread))} below the typical box, so a few bad boxes are pulling the day down rather than pricing across the board.`
                : h.spread > 8
                  ? `The average sits ${usd(h.spread)} above the typical box, so a few strong boxes are carrying the day.`
                  : 'The average and the typical box are close, so this is a broad picture rather than a few outliers.'
            }
          >
            <table className="product-table">
              <thead><tr><th>Order</th><th>Service</th><th style={{ textAlign: 'right' }}>Revenue</th><th style={{ textAlign: 'right' }}>Profit</th></tr></thead>
              <tbody>
                {h.worst.map((o) => (
                  <tr key={o.orderId}>
                    <td>#{o.bcOrderId || o.orderId} <span style={{ color: 'var(--gray-500)' }}>{o.name}{o.state ? `, ${o.state}` : ''}</span></td>
                    <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>{o.shipType}</td>
                    <td style={{ textAlign: 'right' }}>{usd(o.revenue)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: signColor(o.profit) }}>
                      {usd(o.profit)}
                      {o.costsEntered ? null : <div style={{ fontSize: 10, color: WARN, fontWeight: 400 }}>no costs yet</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 2 — products */}
          <Section title="Where product margin went" subtitle="Worst margin first. Percent is the pricing signal; the dollars show how much of the day it moved.">
            {report.products.length ? (
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Product</th><th style={{ textAlign: 'right' }}>Units</th><th style={{ textAlign: 'right' }}>Charged</th>
                    <th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>Margin $</th><th style={{ textAlign: 'right' }}>Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {report.products.map((p, i) => (
                    <tr key={`${p.catalogId || p.name}-${i}`}>
                      <td>
                        {p.name}
                        {p.linesWithoutCost ? <div style={{ fontSize: 10, color: WARN }}>{p.linesWithoutCost} line(s) with no cost recorded</div> : null}
                      </td>
                      <td style={{ textAlign: 'right' }}>{p.units}</td>
                      <td style={{ textAlign: 'right' }}>{usd(p.charged)}</td>
                      <td style={{ textAlign: 'right' }}>{usd(p.cost)}</td>
                      <td style={{ textAlign: 'right', color: signColor(p.marginDollars) }}>{usd(p.marginDollars)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: marginColor(p.marginPct) }}>{pctTxt(p.marginPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>No priced product lines on this day.</div>}
          </Section>

          {/* 3 — shipping */}
          <Section
            title="Where shipping margin went"
            subtitle="What the customer paid to ship against what shipping that box cost us. Saturday and AK/HI surcharges count on both sides."
          >
            <div style={{ fontSize: 13, marginBottom: 12, color: 'var(--gray-700)', lineHeight: 1.6 }}>
              On the {report.shipping.totals.priced} boxes charged for shipping, customers paid <strong>{usd(report.shipping.totals.charged)}</strong> against
              a cost of <strong>{usd(report.shipping.totals.cost)}</strong> — <strong style={{ color: signColor(report.shipping.totals.gap) }}>{usd(report.shipping.totals.gap)}</strong>{' '}
              {report.shipping.totals.gap < 0 ? 'short' : 'ahead'}. {report.shipping.totals.under} were charged less than they cost.
              {report.shipping.totals.freeShipOrders ? ` A further ${report.shipping.totals.freeShipOrders} shipped free, costing ${usd(report.shipping.totals.freeShipCost)}.` : ''}
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
                Cost here is the scale cost captured when the box shipped, not the UPS invoice — that arrives weekly. The two normally sit within about ten cents.
              </div>
            </div>
            {report.shipping.worst.length ? (
              <table className="product-table">
                <thead>
                  <tr><th>Order</th><th>To</th><th>Service</th><th style={{ textAlign: 'right' }}>Charged</th><th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>Gap</th></tr>
                </thead>
                <tbody>
                  {report.shipping.worst.slice(0, 40).map((o) => (
                    <tr key={o.orderId}>
                      <td>#{o.bcOrderId || o.orderId}</td>
                      <td style={{ color: 'var(--gray-500)' }}>{o.name}{o.state ? `, ${o.state}` : ''}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>{o.shipType}</td>
                      <td style={{ textAlign: 'right' }}>{usd(o.charged)}</td>
                      <td style={{ textAlign: 'right' }}>{usd(o.cost)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: signColor(o.gap) }}>{usd(o.gap)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Every box charged for shipping covered its cost.</div>}
          </Section>

          {/* 4 — known losses */}
          <Section
            title="Expected losses, kept out of the numbers above"
            subtitle="Separated so a bad margin day cannot be waved off as gift cards, and so a giveaway is never read as a pricing fault."
          >
            <table className="product-table">
              <tbody>
                <tr>
                  <td><strong>Paid gift cards redeemed</strong>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {report.knownLoss.giftCards.orders} box(es). The money came in when the card was bought, so a thin box here is expected.
                    </div></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 15 }}>{usd(report.knownLoss.giftCards.amount)}</td>
                </tr>
                <tr>
                  <td><strong>VIP rewards redeemed</strong>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {report.knownLoss.vipRewards.measured
                        ? `${report.knownLoss.vipRewards.orders} box(es). A promotional giveaway — no money was ever collected for this.`
                        : `Could not be read for this day, so this is unknown rather than zero.`}
                    </div></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, color: 'var(--orange)' }}>
                    {report.knownLoss.vipRewards.measured ? usd(report.knownLoss.vipRewards.amount) : 'not measured'}
                  </td>
                </tr>
                <tr>
                  <td><strong>Items given away</strong>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {report.knownLoss.giveaways.list.length
                        ? `${report.knownLoss.giveaways.lines} free line(s): ${report.knownLoss.giveaways.list.slice(0, 3).map((g) => `${g.name} x${g.units} (${usd(g.cost)})`).join(', ')}`
                        : 'None on this day.'}
                    </div></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, color: 'var(--orange)' }}>{usd(report.knownLoss.giveaways.cost)}</td>
                </tr>
                <tr>
                  <td><strong>Reships</strong>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                      {report.knownLoss.reships.orders} box(es) sent with no revenue against them. Held out of the figures above.
                    </div></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, color: BAD }}>{usd(Math.abs(report.knownLoss.reships.cost))}</td>
                </tr>
              </tbody>
            </table>
          </Section>

          {/* 5 — trend */}
          <Section title="Day over day" subtitle="Average and typical profit per box across recent shipping days.">
            <table className="product-table">
              <thead>
                <tr><th>Day</th><th style={{ textAlign: 'right' }}>Boxes</th><th style={{ textAlign: 'right' }}>Average</th><th style={{ textAlign: 'right' }}>Typical</th><th style={{ width: '35%' }}></th></tr>
              </thead>
              <tbody>
                {report.trend.map((p) => (
                  <tr key={p.date} style={{ cursor: 'pointer' }} onClick={() => { const d = parseUs(p.date); if (d) setDate(d); }}>
                    <td>
                      {p.date}
                      {p.unmeasured ? <div style={{ fontSize: 10, color: WARN }}>costs not entered ({p.costsEntered}/{p.orders}) — not comparable</div>
                        : p.provisional ? <div style={{ fontSize: 10, color: WARN }}>provisional ({p.costsEntered}/{p.orders} costed)</div> : null}
                    </td>
                    <td style={{ textAlign: 'right' }}>{p.orders}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: p.unmeasured ? 'var(--gray-400)' : signColor(p.meanPpb) }}>{usd(p.meanPpb)}</td>
                    <td style={{ textAlign: 'right' }}>{usd(p.medianPpb)}</td>
                    <td>
                      <div style={{
                        background: p.unmeasured ? 'var(--gray-200)' : 'var(--orange)',
                        height: 8, borderRadius: 4,
                        width: `${Math.max(2, Math.round((Math.max(p.meanPpb, 0) / trendMax) * 100))}%`,
                      }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}
